import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { api, ApiError, asList } from '../lib/api';
import type {
  CampusView, InstitutionView, AcademicClassView, SubjectView,
  ExamView, ExamRosterView, SemesterResultReport, StudentSemesterResult, VtuConfigView,
  PuResultReport, StudentPuResult, PuConfigView,
  ClassCgpaReport, StudentCgpaView, PublishSemesterResult,
  CbseResultReport, StudentCbseResult, CbseConfigView,
  BacklogReport, BacklogRow,
} from '../lib/types';

type Tab = 'exams' | 'results' | 'cgpa' | 'config';
const COMPONENTS = [
  { value: 'Cie', label: 'CIE (internal)' },
  { value: 'See', label: 'SEE (semester-end)' },
];
const STATE_STYLE: Record<string, string> = {
  Pass: 'pill-ok', Fail: 'pill-danger', Awaited: 'pill-muted', Absent: 'pill-warn',
};

export function ExamsPage() {
  const [tab, setTab] = useState<Tab>('exams');
  const [institutions, setInstitutions] = useState<InstitutionView[]>([]);
  const [campuses, setCampuses] = useState<CampusView[]>([]);
  const [institutionId, setInstitutionId] = useState('');

  useEffect(() => {
    Promise.all([
      api.get<InstitutionView[]>('/api/institutions'),
      api.get<CampusView[]>('/api/campuses'),
    ]).then(([i, c]) => { setInstitutions(i); setCampuses(c); if (i.length === 1) setInstitutionId(i[0].id); }).catch(() => {});
  }, []);

  const campusName = (id: string) => campuses.find((c) => c.id === id)?.name ?? '';

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Engineering</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Exams &amp; results</h1>
        <p style={{ color: 'var(--ink-2)', marginTop: 4, maxWidth: 640 }}>
          Record CIE and SEE marks, and compute per-subject grades and semester SGPA against
          the institution’s grading rules. Incomplete results show as “awaited”, never a fabricated fail.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
        <select className="input" value={institutionId} onChange={(e) => setInstitutionId(e.target.value)} style={{ maxWidth: 320 }}>
          <option value="">Select an institution</option>
          {institutions.map((i) => <option key={i.id} value={i.id}>{i.name}{campusName(i.campusId) ? ` · ${campusName(i.campusId)}` : ''}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          <TabBtn active={tab === 'exams'} onClick={() => setTab('exams')}>Exams</TabBtn>
          <TabBtn active={tab === 'results'} onClick={() => setTab('results')}>Results</TabBtn>
          <TabBtn active={tab === 'cgpa'} onClick={() => setTab('cgpa')}>CGPA</TabBtn>
          <TabBtn active={tab === 'config'} onClick={() => setTab('config')}>Grading</TabBtn>
        </div>
      </div>

      {!institutionId ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-2)' }}>Select an institution to begin.</div>
      ) : tab === 'exams' ? <Exams institutionId={institutionId} />
        : tab === 'results' ? <Results institutionId={institutionId} />
        : tab === 'cgpa' ? <Cgpa institutionId={institutionId} />
        : <Config institutionId={institutionId} />}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button className="btn" onClick={onClick} style={{
      padding: '7px 14px', background: active ? 'var(--brand)' : 'var(--surface)',
      color: active ? '#fff' : 'var(--ink-2)', borderColor: active ? 'var(--brand)' : 'var(--line)',
    }}>{children}</button>
  );
}

// ---------------- CGPA tab ----------------
function Cgpa({ institutionId }: { institutionId: string }) {
  const [classes, setClasses] = useState<AcademicClassView[]>([]);
  const [classId, setClassId] = useState('');
  const [semester, setSemester] = useState('1');
  const [report, setReport] = useState<ClassCgpaReport | null>(null);
  const [backlogs, setBacklogs] = useState<BacklogReport | null>(null);
  const [recording, setRecording] = useState<BacklogRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    api.get<AcademicClassView[]>(`/api/classes?institutionId=${institutionId}`).then((r) => setClasses(asList(r))).catch(() => {});
  }, [institutionId]);

  const loadReport = useCallback(async () => {
    if (!classId) { setReport(null); setBacklogs(null); return; }
    setLoading(true);
    try {
      setReport(await api.get<ClassCgpaReport>(`/api/exams/cgpa?academicClassId=${classId}`));
      setBacklogs(await api.get<BacklogReport>(`/api/exams/backlogs?academicClassId=${classId}&semester=${semester}`));
    } finally { setLoading(false); }
  }, [classId, semester]);
  useEffect(() => { loadReport(); }, [loadReport]);

  const publish = async () => {
    if (!classId) return;
    setMsg(null);
    const res = await api.post<PublishSemesterResult>('/api/exams/publish-semester', {
      academicClassId: classId, semester: Number(semester),
    });
    setMsg(`Published ${res.published} student${res.published === 1 ? '' : 's'}` +
      (res.withheld ? `, withheld ${res.withheld} (awaited results or backlog policy).` : '.'));
    loadReport();
  };

  return (
    <div>
      <div className="card" style={{ padding: 16, marginBottom: 16, background: 'var(--surface-2)' }}>
        <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 12 }}>
          <strong>Publishing</strong> freezes a semester’s SGPA as the input to CGPA. CGPA aggregates only
          published semesters, so it won’t shift if an old mark is later edited — re-publish to update.
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label className="label">Class</label>
            <select className="input" value={classId} onChange={(e) => setClassId(e.target.value)} style={{ minWidth: 200 }}>
              <option value="">Select</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Semester to publish</label>
            <select className="input" value={semester} onChange={(e) => setSemester(e.target.value)}>
              {[1,2,3,4,5,6,7,8].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <button className="btn btn-accent" onClick={publish} disabled={!classId}>Publish semester {semester}</button>
        </div>
        {msg && <div className="pill pill-ok" style={{ padding: '6px 12px', borderRadius: 'var(--r-md)', marginTop: 12, justifyContent: 'flex-start' }}>{msg}</div>}
      </div>

      {loading && <p style={{ color: 'var(--ink-3)' }}>Loading…</p>}

      {backlogs && backlogs.backlogs.length > 0 && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div className="eyebrow" style={{ marginBottom: 4 }}>Backlogs · semester {backlogs.semester}</div>
          <p style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>
            Subjects failed this semester. Recording a cleared re-attempt recomputes the SGPA
            with the configured cap — re-publish the semester afterwards to update CGPA.
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ textAlign: 'left' }}>
              <Th>Student</Th><Th>Subject</Th><Th></Th>
            </tr></thead>
            <tbody>
              {backlogs.backlogs.map((b) => (
                <tr key={`${b.studentId}-${b.subjectId}`} style={{ borderTop: '1px solid var(--line-2)' }}>
                  <Td>{b.fullName}</Td>
                  <Td><span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{b.subjectCode}</span> {b.subjectName}</Td>
                  <Td align="right">
                    {b.hasReattempt
                      ? <span className="pill pill-ok" style={{ fontSize: 11 }}>re-attempt recorded</span>
                      : <button className="btn" style={{ padding: '4px 12px', fontSize: 13 }} onClick={() => setRecording(b)}>Record re-attempt</button>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {report && (
        report.students.length === 0
          ? <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--ink-2)' }}>No students linked to this class.</div>
          : <div style={{ display: 'grid', gap: 10 }}>{report.students.map((s) => <CgpaCard key={s.studentId} s={s} />)}</div>
      )}

      {recording && (
        <RecordReattemptDialog backlog={recording} classId={classId} semester={Number(semester)}
          onClose={() => setRecording(null)} onDone={() => { setRecording(null); loadReport(); }} />
      )}
    </div>
  );
}

function RecordReattemptDialog({ backlog, classId, semester, onClose, onDone }:
  { backlog: BacklogRow; classId: string; semester: number; onClose: () => void; onDone: () => void }) {
  const [cie, setCie] = useState('');
  const [see, setSee] = useState('');
  const [absent, setAbsent] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null); setBusy(true);
    try {
      await api.post('/api/exams/reattempts', {
        studentId: backlog.studentId, subjectId: backlog.subjectId, academicClassId: classId, semester,
        cie: absent || cie === '' ? null : Number(cie),
        see: absent || see === '' ? null : Number(see),
        isAbsent: absent, attemptDate: date,
      });
      onDone();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not record the re-attempt.'); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <div className="eyebrow" style={{ marginBottom: 6 }}>Re-attempt · {backlog.subjectCode}</div>
      <h2 className="h-display" style={{ fontSize: 20, marginBottom: 4 }}>{backlog.fullName}</h2>
      <p style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 16 }}>
        Enter the re-attempt marks. A cleared re-attempt is graded from these marks but its
        grade point is capped per the grading rules. The original result is preserved.
      </p>
      {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <label className="label">CIE</label>
          <input className="input mono" type="number" value={cie} disabled={absent} onChange={(e) => setCie(e.target.value)} placeholder="—" />
        </div>
        <div style={{ flex: 1 }}>
          <label className="label">SEE</label>
          <input className="input mono" type="number" value={see} disabled={absent} onChange={(e) => setSee(e.target.value)} placeholder="—" />
        </div>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer', fontSize: 14 }}>
        <input type="checkbox" checked={absent} onChange={(e) => setAbsent(e.target.checked)} /> Absent for the re-attempt
      </label>
      <label className="label">Attempt date</label>
      <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ marginBottom: 20 }} />
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
        <button className="btn btn-accent" style={{ flex: 1, justifyContent: 'center' }} disabled={busy} onClick={submit}>
          {busy ? 'Recording…' : 'Record re-attempt'}
        </button>
      </div>
    </Overlay>
  );
}

function CgpaCard({ s }: { s: StudentCgpaView }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <button onClick={() => setOpen((o) => !o)} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', border: 'none', background: 'transparent', padding: '14px 18px', cursor: 'pointer' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>{s.fullName}</div>
          <div className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
            {s.admissionNumber} · {s.semesterCount} sem · {s.totalCredits} credits
          </div>
        </div>
        {s.anyBacklogs && <span className="pill pill-warn" style={{ fontSize: 11 }}>backlog</span>}
        <div style={{ textAlign: 'right' }}>
          <div className="mono" style={{ fontSize: 20, fontWeight: 700 }}>{s.cgpa != null ? s.cgpa.toFixed(2) : '—'}</div>
          <div className="eyebrow" style={{ fontSize: 9 }}>CGPA</div>
        </div>
      </button>
      {open && s.semesters.length > 0 && (
        <div style={{ borderTop: '1px solid var(--line)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ textAlign: 'left' }}>
              <Th>Semester</Th><Th align="right">SGPA</Th><Th align="right">Credits</Th><Th>Notes</Th>
            </tr></thead>
            <tbody>
              {s.semesters.map((sem) => (
                <tr key={sem.semester} style={{ borderTop: '1px solid var(--line-2)' }}>
                  <Td>Semester {sem.semester}</Td>
                  <Td align="right"><span className="mono" style={{ fontWeight: 700 }}>{sem.sgpa.toFixed(2)}</span></Td>
                  <Td align="right"><span className="mono">{sem.credits}</span></Td>
                  <Td>
                    {sem.hasBacklogs && <span className="pill pill-warn" style={{ fontSize: 10, marginRight: 4 }}>backlog</span>}
                    {sem.revision > 1 && <span className="pill pill-muted" style={{ fontSize: 10 }}>rev {sem.revision}</span>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------- Exams tab ----------------
function Exams({ institutionId }: { institutionId: string }) {
  const [classes, setClasses] = useState<AcademicClassView[]>([]);
  const [subjects, setSubjects] = useState<SubjectView[]>([]);
  const [classId, setClassId] = useState('');
  const [exams, setExams] = useState<ExamView[]>([]);
  const [creating, setCreating] = useState(false);
  const [marking, setMarking] = useState<ExamView | null>(null);

  useEffect(() => {
    api.get<AcademicClassView[]>(`/api/classes?institutionId=${institutionId}`).then((r) => setClasses(asList(r))).catch(() => {});
    api.get<SubjectView[]>(`/api/subjects?institutionId=${institutionId}`).then((r) => setSubjects(asList(r))).catch(() => {});
  }, [institutionId]);

  const load = useCallback(async () => {
    if (!classId) { setExams([]); return; }
    setExams(await api.get<ExamView[]>(`/api/exams?academicClassId=${classId}`));
  }, [classId]);
  useEffect(() => { load(); }, [load]);

  const subjName = (id: string) => subjects.find((s) => s.id === id)?.code ?? '—';

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label className="label">Class</label>
          <select className="input" value={classId} onChange={(e) => setClassId(e.target.value)} style={{ minWidth: 200 }}>
            <option value="">Select a class</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        {classId && <button className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={() => setCreating(true)}>+ New exam</button>}
      </div>

      {classId && (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--line)', textAlign: 'left' }}>
              <Th>Exam</Th><Th>Subject</Th><Th>Component</Th><Th align="right">Max</Th><Th>Date</Th><Th></Th>
            </tr></thead>
            <tbody>
              {exams.map((e) => (
                <tr key={e.id} style={{ borderBottom: '1px solid var(--line-2)' }}>
                  <Td><strong>{e.name}</strong></Td>
                  <Td><span className="mono">{subjName(e.subjectId)}</span></Td>
                  <Td><span className="pill pill-muted" style={{ fontSize: 11 }}>{e.component}</span></Td>
                  <Td align="right"><span className="mono">{e.maxMarks}</span></Td>
                  <Td>{e.date}</Td>
                  <Td align="right"><button className="btn" style={{ padding: '4px 12px', fontSize: 13 }} onClick={() => setMarking(e)}>Enter marks</button></Td>
                </tr>
              ))}
              {exams.length === 0 && <tr><Td colSpan={6} muted>No exams for this class yet.</Td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {creating && <CreateExamDialog classId={classId} subjects={subjects} onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
      {marking && <MarkExamDrawer exam={marking} subjectCode={subjName(marking.subjectId)} onClose={() => setMarking(null)} onDone={() => setMarking(null)} />}
    </div>
  );
}

function CreateExamDialog({ classId, subjects, onClose, onDone }: { classId: string; subjects: SubjectView[]; onClose: () => void; onDone: () => void }) {
  const [subjectId, setSubjectId] = useState('');
  const [name, setName] = useState('');
  const [component, setComponent] = useState('Cie');
  const [maxMarks, setMaxMarks] = useState('50');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null); setBusy(true);
    try {
      await api.post('/api/exams', { academicClassId: classId, subjectId, name: name.trim(), component, maxMarks: Number(maxMarks), date });
      onDone();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not create the exam.'); }
    finally { setBusy(false); }
  };

  return (
    <Overlay onClose={onClose}>
      <h2 className="h-display" style={{ fontSize: 20, marginBottom: 16 }}>New exam</h2>
      {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}
      <label className="label">Subject</label>
      <select className="input" value={subjectId} onChange={(e) => setSubjectId(e.target.value)} style={{ marginBottom: 12 }}>
        <option value="">Select a subject</option>
        {subjects.map((s) => <option key={s.id} value={s.id}>{s.code} — {s.name}</option>)}
      </select>
      <label className="label">Name</label>
      <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="CIE-1" style={{ marginBottom: 12 }} />
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <label className="label">Component</label>
          <select className="input" value={component} onChange={(e) => setComponent(e.target.value)}>
            {COMPONENTS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div style={{ width: 100 }}>
          <label className="label">Max marks</label>
          <input className="input mono" type="number" value={maxMarks} onChange={(e) => setMaxMarks(e.target.value)} />
        </div>
      </div>
      <label className="label">Date</label>
      <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ marginBottom: 20 }} />
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
        <button className="btn btn-accent" style={{ flex: 1, justifyContent: 'center' }} disabled={!subjectId || !name.trim() || busy} onClick={submit}>
          {busy ? 'Creating…' : 'Create exam'}
        </button>
      </div>
    </Overlay>
  );
}

function MarkExamDrawer({ exam, subjectCode, onClose, onDone }: { exam: ExamView; subjectCode: string; onClose: () => void; onDone: () => void }) {
  const [roster, setRoster] = useState<ExamRosterView | null>(null);
  const [marks, setMarks] = useState<Record<string, string>>({});
  const [absent, setAbsent] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get<ExamRosterView>(`/api/exams/${exam.id}/roster`).then((r) => {
      setRoster(r);
      setMarks(Object.fromEntries(r.students.map((s) => [s.studentId, s.marks?.toString() ?? ''])));
      setAbsent(Object.fromEntries(r.students.map((s) => [s.studentId, s.isAbsent])));
    }).catch((e) => setError(e instanceof ApiError ? e.message : 'Could not load the roster.'));
  }, [exam.id]);

  const save = async () => {
    if (!roster) return;
    setBusy(true); setError(null);
    try {
      const entries = roster.students.map((s) => ({
        studentId: s.studentId,
        marks: absent[s.studentId] || marks[s.studentId] === '' ? null : Number(marks[s.studentId]),
        isAbsent: !!absent[s.studentId],
      }));
      await api.post(`/api/exams/${exam.id}/marks`, { entries });
      setSaved(true);
      setTimeout(onDone, 600);
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not save marks.'); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 50, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: 480, maxWidth: '100%', height: '100%', borderRadius: 0, padding: 24, overflowY: 'auto' }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Enter marks · out of {exam.maxMarks}</div>
        <h2 className="h-display" style={{ fontSize: 20, marginBottom: 16 }}>{subjectCode} · {exam.name}</h2>
        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}
        {saved && <div className="pill pill-ok" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', marginBottom: 12, justifyContent: 'flex-start' }}>Saved.</div>}

        {roster && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 20 }}>
            {roster.students.map((s) => (
              <div key={s.studentId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--line-2)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.fullName}</div>
                  <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{s.admissionNumber}</div>
                </div>
                <input className="input mono" type="number" min={0} max={exam.maxMarks}
                  value={absent[s.studentId] ? '' : (marks[s.studentId] ?? '')}
                  disabled={absent[s.studentId]}
                  onChange={(e) => setMarks((m) => ({ ...m, [s.studentId]: e.target.value }))}
                  style={{ width: 72, textAlign: 'center', padding: '6px 4px' }} placeholder="—" />
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--ink-3)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!absent[s.studentId]} onChange={(e) => setAbsent((a) => ({ ...a, [s.studentId]: e.target.checked }))} />
                  AB
                </label>
              </div>
            ))}
            {roster.students.length === 0 && <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: '12px 0' }}>No students linked to this class.</div>}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Close</button>
          <button className="btn btn-accent" style={{ flex: 1, justifyContent: 'center' }} disabled={busy || !roster || roster.students.length === 0} onClick={save}>
            {busy ? 'Saving…' : 'Save marks'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Results tab ----------------
function Results({ institutionId }: { institutionId: string }) {
  const [mode, setMode] = useState<'vtu' | 'pu' | 'cbse'>('vtu');
  const [classes, setClasses] = useState<AcademicClassView[]>([]);
  const [classId, setClassId] = useState('');
  const [semester, setSemester] = useState('1');
  const [report, setReport] = useState<SemesterResultReport | null>(null);
  const [puReport, setPuReport] = useState<PuResultReport | null>(null);
  const [cbseReport, setCbseReport] = useState<CbseResultReport | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get<AcademicClassView[]>(`/api/classes?institutionId=${institutionId}`).then((r) => setClasses(asList(r))).catch(() => {});
  }, [institutionId]);

  const clearAll = () => { setReport(null); setPuReport(null); setCbseReport(null); };

  const run = useCallback(async () => {
    if (!classId) return;
    setLoading(true); setReport(null); setPuReport(null); setCbseReport(null);
    try {
      if (mode === 'vtu')
        setReport(await api.get<SemesterResultReport>(`/api/exams/results?academicClassId=${classId}&semester=${semester}`));
      else if (mode === 'pu')
        setPuReport(await api.get<PuResultReport>(`/api/exams/pu-results?academicClassId=${classId}`));
      else
        setCbseReport(await api.get<CbseResultReport>(`/api/exams/cbse-results?academicClassId=${classId}`));
    } finally { setLoading(false); }
  }, [classId, semester, mode]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <ModeBtn active={mode === 'vtu'} onClick={() => { setMode('vtu'); clearAll(); }}>VTU (SGPA)</ModeBtn>
        <ModeBtn active={mode === 'pu'} onClick={() => { setMode('pu'); clearAll(); }}>PU (marks)</ModeBtn>
        <ModeBtn active={mode === 'cbse'} onClick={() => { setMode('cbse'); clearAll(); }}>CBSE (grades)</ModeBtn>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label className="label">Class</label>
          <select className="input" value={classId} onChange={(e) => setClassId(e.target.value)} style={{ minWidth: 200 }}>
            <option value="">Select</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        {mode === 'vtu' && (
          <div>
            <label className="label">Semester</label>
            <select className="input" value={semester} onChange={(e) => setSemester(e.target.value)}>
              {[1,2,3,4,5,6,7,8].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        )}
        <button className="btn btn-primary" onClick={run} disabled={!classId || loading}>{loading ? 'Running…' : 'Run results'}</button>
      </div>

      {mode === 'cbse' && cbseReport && (
        cbseReport.students.length === 0
          ? <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--ink-2)' }}>No students linked to this class.</div>
          : <div style={{ display: 'grid', gap: 10 }}>{cbseReport.students.map((s) => <CbseResultCard key={s.studentId} s={s} />)}</div>
      )}

      {mode === 'vtu' && report && (
        report.students.length === 0
          ? <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--ink-2)' }}>No students linked to this class.</div>
          : <div style={{ display: 'grid', gap: 10 }}>{report.students.map((s) => <ResultCard key={s.studentId} s={s} />)}</div>
      )}

      {mode === 'pu' && puReport && (
        puReport.students.length === 0
          ? <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--ink-2)' }}>No students linked to this class.</div>
          : <div style={{ display: 'grid', gap: 10 }}>{puReport.students.map((s) => <PuResultCard key={s.studentId} s={s} />)}</div>
      )}
    </div>
  );
}

function ModeBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button onClick={onClick} className="btn" style={{
      padding: '5px 12px', fontSize: 13,
      background: active ? 'var(--accent)' : 'var(--surface)',
      color: active ? 'var(--accent-ink, #1a1200)' : 'var(--ink-2)',
      borderColor: active ? 'var(--accent)' : 'var(--line)',
    }}>{children}</button>
  );
}

function PuResultCard({ s }: { s: StudentPuResult }) {
  const [open, setOpen] = useState(false);
  const vstyle = s.verdict === 'Pass' ? 'pill-ok' : s.verdict === 'Fail' ? 'pill-danger' : 'pill-muted';
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <button onClick={() => setOpen((o) => !o)} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', border: 'none', background: 'transparent', padding: '14px 18px', cursor: 'pointer' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>{s.fullName}</div>
          <div className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{s.admissionNumber}</div>
        </div>
        <span className={`pill ${vstyle}`}>{s.verdict}{s.provisional && s.verdict === 'Awaited' ? '' : ''}</span>
        <div style={{ textAlign: 'right', minWidth: 62 }}>
          <div className="mono" style={{ fontSize: 18, fontWeight: 700 }}>{s.aggregatePercent != null ? `${s.aggregatePercent}%` : '—'}</div>
          <div className="eyebrow" style={{ fontSize: 9 }}>Aggregate{s.provisional ? ' · prov.' : ''}</div>
        </div>
      </button>
      {open && (
        <div style={{ borderTop: '1px solid var(--line)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ textAlign: 'left' }}>
              <Th>Subject</Th><Th align="right">Marks</Th><Th align="right">Max</Th><Th align="right">%</Th><Th>Result</Th>
            </tr></thead>
            <tbody>
              {s.subjects.map((sub) => (
                <tr key={sub.subjectId} style={{ borderTop: '1px solid var(--line-2)' }}>
                  <Td><span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{sub.subjectCode}</span> {sub.subjectName}</Td>
                  <Td align="right"><span className="mono">{sub.marks ?? '—'}</span></Td>
                  <Td align="right"><span className="mono">{sub.max}</span></Td>
                  <Td align="right"><span className="mono" style={{ fontWeight: 700 }}>{sub.state === 'Awaited' ? '—' : `${sub.percent}%`}</span></Td>
                  <Td><span className={`pill ${STATE_STYLE[sub.state] ?? 'pill-muted'}`} style={{ fontSize: 11 }}>{sub.state}</span></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CbseResultCard({ s }: { s: StudentCbseResult }) {
  const [open, setOpen] = useState(false);
  const vstyle = s.verdict === 'Pass' ? 'pill-ok' : s.verdict === 'Fail' ? 'pill-danger' : 'pill-muted';
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <button onClick={() => setOpen((o) => !o)} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', border: 'none', background: 'transparent', padding: '14px 18px', cursor: 'pointer' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>{s.fullName}</div>
          <div className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{s.admissionNumber}</div>
        </div>
        <span className={`pill ${vstyle}`}>{s.verdict}</span>
      </button>
      {open && (
        <div style={{ borderTop: '1px solid var(--line)', padding: '4px 0' }}>
          <div className="eyebrow" style={{ fontSize: 10, padding: '8px 16px 4px', color: 'var(--ink-3)' }}>Scholastic</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ textAlign: 'left' }}>
              <Th>Subject</Th><Th align="right">Marks</Th><Th align="right">%</Th><Th align="right">Grade</Th><Th>Result</Th>
            </tr></thead>
            <tbody>
              {s.scholastic.map((sub) => (
                <tr key={sub.subjectId} style={{ borderTop: '1px solid var(--line-2)' }}>
                  <Td><span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{sub.subjectCode}</span> {sub.subjectName}</Td>
                  <Td align="right"><span className="mono">{sub.marks ?? '—'}</span></Td>
                  <Td align="right"><span className="mono">{sub.state === 'Awaited' ? '—' : `${sub.percent}%`}</span></Td>
                  <Td align="right"><span className="mono" style={{ fontWeight: 700 }}>{sub.state === 'Awaited' ? '—' : sub.grade}</span></Td>
                  <Td><span className={`pill ${STATE_STYLE[sub.state] ?? 'pill-muted'}`} style={{ fontSize: 11 }}>{sub.state}</span></Td>
                </tr>
              ))}
              {s.scholastic.length === 0 && <tr><Td colSpan={5} muted>No scholastic subjects.</Td></tr>}
            </tbody>
          </table>

          {s.coScholastic.length > 0 && (
            <>
              <div className="eyebrow" style={{ fontSize: 10, padding: '12px 16px 4px', color: 'var(--ink-3)' }}>Co-scholastic</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ textAlign: 'left' }}><Th>Area</Th><Th align="right">Grade</Th></tr></thead>
                <tbody>
                  {s.coScholastic.map((sub) => (
                    <tr key={sub.subjectId} style={{ borderTop: '1px solid var(--line-2)' }}>
                      <Td>{sub.subjectName}</Td>
                      <Td align="right"><span className="mono" style={{ fontWeight: 700 }}>{sub.grade ?? '—'}</span></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ResultCard({ s }: { s: StudentSemesterResult }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <button onClick={() => setOpen((o) => !o)} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', border: 'none', background: 'transparent', padding: '14px 18px', cursor: 'pointer' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>{s.fullName}</div>
          <div className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{s.admissionNumber}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="mono" style={{ fontSize: 20, fontWeight: 700 }}>{s.sgpa != null ? s.sgpa.toFixed(2) : '—'}</div>
          <div className="eyebrow" style={{ fontSize: 9 }}>SGPA{s.provisional ? ' · provisional' : ''}</div>
        </div>
      </button>
      {open && (
        <div style={{ borderTop: '1px solid var(--line)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ textAlign: 'left' }}>
              <Th>Subject</Th><Th align="right">Cr</Th><Th align="right">CIE</Th><Th align="right">SEE</Th><Th align="right">Total</Th><Th align="right">Grade</Th><Th>Result</Th>
            </tr></thead>
            <tbody>
              {s.subjects.map((sub) => (
                <tr key={sub.subjectId} style={{ borderTop: '1px solid var(--line-2)' }}>
                  <Td><span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{sub.subjectCode}</span> {sub.subjectName}</Td>
                  <Td align="right"><span className="mono">{sub.credits}</span></Td>
                  <Td align="right"><span className="mono">{sub.cie ?? '—'}</span></Td>
                  <Td align="right"><span className="mono">{sub.see ?? '—'}</span></Td>
                  <Td align="right"><span className="mono">{sub.total ?? '—'}</span></Td>
                  <Td align="right"><span className="mono" style={{ fontWeight: 700 }}>{sub.state === 'Awaited' ? '—' : `${sub.letter} (${sub.gradePoint})`}</span>{sub.isReattempt && <span className="pill pill-muted" style={{ fontSize: 9, marginLeft: 6 }}>re-attempt</span>}</Td>
                  <Td><span className={`pill ${STATE_STYLE[sub.state] ?? 'pill-muted'}`} style={{ fontSize: 11 }}>{sub.state}</span></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------- Config tab ----------------
function Config({ institutionId }: { institutionId: string }) {
  const [mode, setMode] = useState<'vtu' | 'pu' | 'cbse'>('vtu');
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <ModeBtn active={mode === 'vtu'} onClick={() => setMode('vtu')}>VTU grade table</ModeBtn>
        <ModeBtn active={mode === 'pu'} onClick={() => setMode('pu')}>PU pass mark</ModeBtn>
        <ModeBtn active={mode === 'cbse'} onClick={() => setMode('cbse')}>CBSE grades</ModeBtn>
      </div>
      {mode === 'vtu' ? <VtuConfig institutionId={institutionId} />
        : mode === 'pu' ? <PuConfig institutionId={institutionId} />
        : <CbseConfig institutionId={institutionId} />}
    </div>
  );
}

function VtuConfig({ institutionId }: { institutionId: string }) {
  const [cfg, setCfg] = useState<VtuConfigView | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setCfg(await api.get<VtuConfigView>(`/api/exams/config/${institutionId}`));
  }, [institutionId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!cfg) return;
    setBusy(true); setMsg(null);
    try {
      const updated = await api.put<VtuConfigView>(`/api/exams/config/${institutionId}`, {
        cieMax: cfg.cieMax, seeMax: cfg.seeMax,
        minSeePercentToPass: cfg.minSeePercentToPass, minTotalPercentToPass: cfg.minTotalPercentToPass,
        publishWithBacklogs: cfg.publishWithBacklogs,
        capReattemptGradePoint: cfg.capReattemptGradePoint,
        reattemptGradePointCap: cfg.reattemptGradePointCap,
        bands: cfg.bands,
      });
      setCfg(updated); setMsg('Saved.');
    } finally { setBusy(false); }
  };

  if (!cfg) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;

  const setField = (k: keyof VtuConfigView, v: number) => setCfg({ ...cfg, [k]: v });
  const setBand = (i: number, k: 'minPercent' | 'gradePoint', v: number) => {
    const bands = cfg.bands.map((b, idx) => idx === i ? { ...b, [k]: v } : b);
    setCfg({ ...cfg, bands });
  };

  return (
    <div style={{ maxWidth: 560 }}>
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 12 }}>Marks split</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="CIE max" value={cfg.cieMax} onChange={(v) => setField('cieMax', v)} />
          <Field label="SEE max" value={cfg.seeMax} onChange={(v) => setField('seeMax', v)} />
          <Field label="Min SEE % to pass" value={cfg.minSeePercentToPass} onChange={(v) => setField('minSeePercentToPass', v)} />
          <Field label="Min total % to pass" value={cfg.minTotalPercentToPass} onChange={(v) => setField('minTotalPercentToPass', v)} />
        </div>
      </div>

      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 12 }}>Grade table (total %)</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ textAlign: 'left' }}><Th>≥ %</Th><Th>Letter</Th><Th align="right">Grade point</Th></tr></thead>
          <tbody>
            {cfg.bands.map((b, i) => (
              <tr key={i} style={{ borderTop: '1px solid var(--line-2)' }}>
                <Td><input className="input mono" type="number" value={b.minPercent} onChange={(e) => setBand(i, 'minPercent', Number(e.target.value))} style={{ width: 70, padding: '4px 6px' }} /></Td>
                <Td><span className="mono" style={{ fontWeight: 600 }}>{b.letter}</span></Td>
                <Td align="right"><input className="input mono" type="number" value={b.gradePoint} onChange={(e) => setBand(i, 'gradePoint', Number(e.target.value))} style={{ width: 60, padding: '4px 6px' }} /></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 12 }}>CGPA / backlog rules</div>
        <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: -4, marginBottom: 14 }}>
          These defaults reflect common VTU practice but were not verified against current
          official regulations — confirm with the university before treating results as final.
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={cfg.publishWithBacklogs} onChange={(e) => setCfg({ ...cfg, publishWithBacklogs: e.target.checked })} />
          <span style={{ fontSize: 14 }}>Allow publishing a semester with unresolved backlogs (failed subject sits at grade point 0)</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, cursor: 'pointer' }}>
          <input type="checkbox" checked={cfg.capReattemptGradePoint} onChange={(e) => setCfg({ ...cfg, capReattemptGradePoint: e.target.checked })} />
          <span style={{ fontSize: 14 }}>Cap grade point on a cleared re-attempt</span>
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 26 }}>
          <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>Re-attempt cap</span>
          <input className="input mono" type="number" min={0} max={10} value={cfg.reattemptGradePointCap}
            disabled={!cfg.capReattemptGradePoint}
            onChange={(e) => setCfg({ ...cfg, reattemptGradePointCap: Number(e.target.value) })} style={{ width: 70, padding: '4px 6px' }} />
        </div>
        <p style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 10 }}>
          Note: the re-attempt cap is stored but applied only once re-attempt tracking is added (a later slice).
        </p>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <button className="btn btn-accent" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save grading rules'}</button>
        {msg && <span className="pill pill-ok" style={{ fontSize: 12 }}>{msg}</span>}
      </div>
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input mono" type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}

function PuConfig({ institutionId }: { institutionId: string }) {
  const [cfg, setCfg] = useState<PuConfigView | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setCfg(await api.get<PuConfigView>(`/api/exams/pu-config/${institutionId}`));
  }, [institutionId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!cfg) return;
    setBusy(true); setMsg(null);
    try {
      const updated = await api.put<PuConfigView>(`/api/exams/pu-config/${institutionId}`, { passPercent: cfg.passPercent });
      setCfg(updated); setMsg('Saved.');
    } finally { setBusy(false); }
  };

  if (!cfg) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;

  return (
    <div style={{ maxWidth: 400 }}>
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 12 }}>Pass mark</div>
        <label className="label">Minimum % to pass a subject</label>
        <input className="input mono" type="number" min={0} max={100} value={cfg.passPercent}
          onChange={(e) => setCfg({ ...cfg, passPercent: Number(e.target.value) })} style={{ width: 120 }} />
        <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8 }}>
          Applied to every subject. A student passes overall only if they pass every subject.
        </p>
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <button className="btn btn-accent" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save pass mark'}</button>
        {msg && <span className="pill pill-ok" style={{ fontSize: 12 }}>{msg}</span>}
      </div>
    </div>
  );
}

function CbseConfig({ institutionId }: { institutionId: string }) {
  const [cfg, setCfg] = useState<CbseConfigView | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setCfg(await api.get<CbseConfigView>(`/api/exams/cbse-config/${institutionId}`));
  }, [institutionId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!cfg) return;
    setBusy(true); setMsg(null);
    try {
      const updated = await api.put<CbseConfigView>(`/api/exams/cbse-config/${institutionId}`, {
        passPercent: cfg.passPercent, coScholasticGrades: cfg.coScholasticGrades, bands: cfg.bands,
      });
      setCfg(updated); setMsg('Saved.');
    } finally { setBusy(false); }
  };

  if (!cfg) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;

  const setBand = (i: number, v: number) => {
    const bands = cfg.bands.map((b, idx) => idx === i ? { ...b, minPercent: v } : b);
    setCfg({ ...cfg, bands });
  };

  return (
    <div style={{ maxWidth: 520 }}>
      <p style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14 }}>
        Defaults reflect the commonly-published CBSE scheme but were not verified against
        current official norms — confirm with the school before treating results as final.
      </p>
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div className="eyebrow" style={{ marginBottom: 12 }}>Scholastic grade table (subject %)</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ textAlign: 'left' }}><Th>≥ %</Th><Th>Grade</Th></tr></thead>
          <tbody>
            {cfg.bands.map((b, i) => (
              <tr key={i} style={{ borderTop: '1px solid var(--line-2)' }}>
                <Td><input className="input mono" type="number" value={b.minPercent} onChange={(e) => setBand(i, Number(e.target.value))} style={{ width: 70, padding: '4px 6px' }} /></Td>
                <Td><span className="mono" style={{ fontWeight: 600 }}>{b.grade}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label className="label">Pass %</label>
            <input className="input mono" type="number" min={0} max={100} value={cfg.passPercent}
              onChange={(e) => setCfg({ ...cfg, passPercent: Number(e.target.value) })} />
          </div>
          <div>
            <label className="label">Co-scholastic grades</label>
            <input className="input mono" value={cfg.coScholasticGrades}
              onChange={(e) => setCfg({ ...cfg, coScholasticGrades: e.target.value })} placeholder="A,B,C" />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <button className="btn btn-accent" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save CBSE grades'}</button>
        {msg && <span className="pill pill-ok" style={{ fontSize: 12 }}>{msg}</span>}
      </div>
    </div>
  );
}

function Overlay({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 50, display: 'grid', placeItems: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: 460, maxWidth: '100%', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
        {children}
      </div>
    </div>
  );
}

function Th({ children, align }: { children?: ReactNode; align?: 'right' }) {
  return <th style={{ padding: '10px 14px', fontSize: 11, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '.02em', textAlign: align ?? 'left' }}>{children}</th>;
}
function Td({ children, colSpan, muted, align }: { children?: ReactNode; colSpan?: number; muted?: boolean; align?: 'right' }) {
  return <td colSpan={colSpan} style={{ padding: '10px 14px', color: muted ? 'var(--ink-3)' : 'var(--ink)', textAlign: muted ? 'center' : (align ?? 'left'), fontSize: 13 }}>{children}</td>;
}
